import React, { useState } from 'react';

import AdvertCard from '@Components/Advert/AdvertCard';
import Button, { ButtonStyles } from '@Components/Buttons/Button';
import DocumentTitle from '@Components/DocumentTitle';
import AdFilters from '@Components/forms/Filter/AdFilters';
import AdvertsSortSelect from '@Components/forms/inputs/selects/AdvertsSortSelect';
import Col from '@Components/layout/Col';
import Container from '@Components/layout/Container';
import GridCol from '@Components/layout/Grid/GridCol';
import GridRow from '@Components/layout/Grid/GridRow';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import MapPreviewButton from '@Components/MapPreviewButton';
import NoResultsContainer from '@Components/NoResultsContainer';
import PaginatedDataBlock from '@Components/PaginatedDataBlock';
import Typography from '@Components/Typography';
import { FETCH_LIMIT } from '@Config/constants';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import {
  GetAdsByCategoryQuery,
  GetAdsByCategoryQueryVariables,
  useGetAdsByCategoryQuery,
  useGetCategoriesQuery,
} from '@Graphql/graphqlTypes.generated';
import { useRouteParams } from '@Hooks/useRouteParams';
import { useSetAppKind } from '@Hooks/useSetAppKind';
import { useTranslateObject } from '@Hooks/useTranslateObject';
import { useTranslations } from '@Hooks/useTranslations';
import CategoryTree from '@Routes/category/CategoryPage/CategoryTree';
import { useAdvertFilterData } from '@Utils/hooks/useAdvertFilterData';
import { convertMoneyToCents } from '@Utils/money';
import { defaultSearchValues } from '@Utils/types';

import styles from './CategoryPage.module.scss';

const CategoryPage: React.FC = () => {
  const selectTranslation = useTranslateObject();
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const t = useTranslations();

  const { data: catData, loading: isLoadingCatData } = useGetCategoriesQuery();
  const categories = catData?.categories || [];

  const { categorySlug } = useRouteParams(RouteConfig.Category);

  const {
    filterData,
    sortData,
    geoSearchWithDistance,
    searchData: { searchBy },
    filtersInitialValues,
    defaultQuerySortValues,
    onFiltersChange,
    onFiltersClear,
    onSortChange,
  } = useAdvertFilterData();

  const { data, fetchMore, loading, networkStatus } = useGetAdsByCategoryQuery({
    variables: {
      categorySlug,
      limit: FETCH_LIMIT,
      ...defaultSearchValues,
      ...defaultQuerySortValues,
      ...geoSearchWithDistance,
      ...filterData,
      searchBy,
      priceGte: convertMoneyToCents(Number(filterData.priceGte)),
      priceLte: convertMoneyToCents(Number(filterData.priceLte)),
      ...sortData,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const selectedCategory = categories.find(item => item.slug === categorySlug);

  useSetAppKind(selectedCategory?.kind);

  const title = selectedCategory ? selectTranslation(selectedCategory.name) : '';
  const adverts = data?.allAdverts;
  const showNoResultsAs = <NoResultsContainer message={Messages.noAdsInCategoryFound} displayActionButton={false} />;

  return (
    <DocumentTitle title={title}>
      <Layout background={LayoutBackgrounds.grayBackground}>
        <Container>
          <Row justifyCenter className={styles.globalWrapper}>
            <Col size={3}>
              <div className={styles.desktopView}>
                <CategoryTree
                  isLoading={isLoadingCatData}
                  categories={categories}
                  selectedCategory={selectedCategory}
                />
              </div>
              <div className={styles.mobileView}>
                <Button
                  style={ButtonStyles.regular}
                  label={t(Messages.btnCategories)}
                  onClick={() => setShowMobileCategories(!showMobileCategories)}
                  className={styles.btnCategories}
                />
                {showMobileCategories && (
                  <CategoryTree
                    isLoading={isLoadingCatData}
                    categories={categories}
                    selectedCategory={selectedCategory}
                  />
                )}
              </div>
            </Col>
            <Col size={9}>
              <GridRow className={styles.adFilters}>
                <GridCol size={12}>
                  <AdFilters
                    onClick={onFiltersClear}
                    onChange={onFiltersChange}
                    initialValues={filtersInitialValues}
                    showDistanceField={filterData.geoSearch}
                    submitOnChange
                    noCategories
                  />
                </GridCol>
              </GridRow>
              <GridRow className={styles.adListGridRowMobile}>
                <GridCol size={12}>
                  <div>
                    <Col className={styles.resultsTitleBlock}>
                      <Row alignCenter>
                        <Typography tag="h2" size="xl" msg={t(Messages.searchResults)} className={styles.countLabel} />
                        <MapPreviewButton
                          edges={data?.allAdverts.edges.map(edge => edge.node)}
                          filterData={filterData}
                        />
                        <AdvertsSortSelect onChange={onSortChange} value={sortData.orderby} />
                      </Row>
                    </Col>
                  </div>
                  <PaginatedDataBlock<GetAdsByCategoryQuery, GetAdsByCategoryQueryVariables>
                    loading={loading}
                    pageInfo={adverts?.pageInfo}
                    dataLength={adverts?.edges.length}
                    fetchMoreKeyword="allAdverts"
                    fetchMore={fetchMore}
                    networkStatus={networkStatus}
                    noResults={showNoResultsAs}
                  >
                    <div className={styles.advertItems}>
                      {adverts?.edges.map(item => {
                        return <AdvertCard advert={item.node} key={item.node.id} />;
                      })}
                    </div>
                  </PaginatedDataBlock>
                </GridCol>
              </GridRow>
            </Col>
          </Row>
        </Container>
      </Layout>
    </DocumentTitle>
  );
};

export default CategoryPage;
